import React from 'react';
import {
  Box,
  Divider,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material';
import {
  DescriptionOutlined as FileIcon,
  Download as DownloadIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import { formatValidNumber } from '../../utils/helper';

const Section = ({ title, children }) => {
  if (children == null || children === '' || (Array.isArray(children) && children.length === 0)) {
    return null;
  }

  return (
    <Box mb={3}>
      <Typography variant='subtitle2' fontWeight={700} color='text.secondary' mb={0.75} textTransform='uppercase' letterSpacing={0.4}>
        {title}
      </Typography>
      {typeof children === 'string' || typeof children === 'number' ? (
        <Typography variant='body2' color='text.primary' sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
          {children}
        </Typography>
      ) : (
        children
      )}
    </Box>
  );
};

const MetricRow = ({ label, value }) => (
  <Box display='flex' justifyContent='space-between' gap={2} py={0.5}>
    <Typography variant='body2' color='text.secondary'>
      {label}
    </Typography>
    <Typography variant='body2' fontWeight={600} textAlign='right'>
      {value ?? 'N/A'}
    </Typography>
  </Box>
);

const displayCategory = (product) => {
  if (!product?.category) return null;
  if (String(product.category).toLowerCase() === 'other') return product.otherCategory || 'Other';
  return product.category;
};

const displayModel = (product) => {
  if (!product?.businessModel) return null;
  if (String(product.businessModel).toLowerCase() === 'other') return product.otherBusinessModel || 'Other';
  return product.businessModel;
};

const displayText = (value) => {
  if (value == null || value === '') return null;
  return value;
};

const ClientWorkspaceOverview = ({ product, company }) => {
  if (!product) return null;

  const companyName = company?.companyName || product.company?.companyName;
  const website = product.website || company?.website || product.company?.website;
  const markets = Array.isArray(product.markets) ? product.markets.join(', ') : product.markets;
  const downloads = product.downloads || [];

  const firstTerm = formatValidNumber(product.commissionFirstTerm);
  const renewals = formatValidNumber(product.commissionRenewals);
  const duration = product.duration || 'No Limit';
  const conversionRate = formatValidNumber(product.averageConversionRate);
  const salesCycle = product.averageSalesCycle || 'N/A';
  const churn = formatValidNumber(product.churn);

  const commissionType = product.commissionType || '';
  const showRecurring =
    commissionType.includes('Recurring') ||
    product.commissionFirstTerm != null ||
    product.commissionRenewals != null;
  const showEventBased = commissionType.includes('Event-Based') || !!product.oneOffCommissions;

  return (
    <Box
      display='grid'
      gridTemplateColumns={{ xs: '1fr', md: '1fr 260px' }}
      gap={3}
      alignItems='start'
    >
      <Box>
        <Section title='Category'>{displayCategory(product)}</Section>
        <Section title='Model'>{displayModel(product)}</Section>
        <Section title='Summary'>{displayText(product.summary)}</Section>
        <Section title='Value Proposition'>{displayText(product.uspsWhyUs)}</Section>
        <Section title='Ideal Customer Profile (ICP)'>{displayText(product.idealCustomerProfile)}</Section>
        <Section title='Key Decision Makers'>{displayText(product.targetProfiles)}</Section>
        <Section title='Blocklist'>{displayText(product.blocklist)}</Section>
        <Section title='Markets'>{displayText(markets)}</Section>

        <Section title='Commissions'>
          <Box
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              p: 1.5,
              bgcolor: 'grey.50',
            }}
          >
            {showRecurring && (
              <Box mb={showEventBased ? 1.5 : 0}>
                <Typography variant='body2' fontWeight={700} mb={0.5}>
                  Recurring Revenue Commission
                </Typography>
                <MetricRow label='First Term (%)' value={firstTerm} />
                <MetricRow label='Renewals (%)' value={renewals} />
                <MetricRow label='Duration (Months)' value={duration} />
              </Box>
            )}
            {showEventBased && (
              <Box>
                <Typography variant='body2' fontWeight={700} mb={0.5}>
                  Event-based Commission
                </Typography>
                <Typography variant='body2' sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
                  {product.oneOffCommissions || 'N/A'}
                </Typography>
              </Box>
            )}
            {!showRecurring && !showEventBased && (
              <Typography variant='body2' color='text.secondary'>
                N/A
              </Typography>
            )}
          </Box>
        </Section>

        <Section title='Performance'>
          <Box
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              p: 1.5,
              bgcolor: 'grey.50',
            }}
          >
            <MetricRow label='Conv. Rate (%)' value={conversionRate} />
            <MetricRow label='Avg. Sales Cycle (wks)' value={salesCycle} />
            <MetricRow label='Churn (%)' value={churn} />
          </Box>
        </Section>

        <Section title='Conditions'>{displayText(product.conditions)}</Section>
        <Section title='Buyout Options'>{displayText(product.buyOutOptions)}</Section>
      </Box>

      <Box
        sx={{
          position: { md: 'sticky' },
          top: 0,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          p: 2,
          bgcolor: 'grey.50',
        }}
      >
        <Typography variant='subtitle2' fontWeight={700} mb={1}>
          Resources
        </Typography>
        {downloads.length > 0 ? (
          <List disablePadding dense>
            {downloads.map((download, index) => (
              <ListItem
                key={index}
                disableGutters
                secondaryAction={
                  <Link href={download.url} target='_blank' rel='noopener noreferrer' download={download.description}>
                    <DownloadIcon fontSize='small' color='primary' />
                  </Link>
                }
                sx={{ py: 1, pr: 5, alignItems: 'flex-start' }}
              >
                <ListItemIcon sx={{ minWidth: 36, mt: 0.75 }}>
                  <FileIcon fontSize='small' color='action' />
                </ListItemIcon>
                <ListItemText
                  primary={download.description || `Download ${index + 1}`}
                  primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                  secondary='PDF'
                  secondaryTypographyProps={{ variant: 'caption' }}
                />
              </ListItem>
            ))}
          </List>
        ) : (
          <Typography variant='body2' color='text.secondary'>
            No downloads available
          </Typography>
        )}

        {website && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant='subtitle2' fontWeight={700} mb={1}>
              Links
            </Typography>
            <Link
              href={website}
              target='_blank'
              rel='noopener noreferrer'
              underline='hover'
              sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, typography: 'body2' }}
            >
              Website
              <OpenInNewIcon sx={{ fontSize: 14 }} />
            </Link>
          </>
        )}
      </Box>
    </Box>
  );
};

export default ClientWorkspaceOverview;
